// LocalStoreAdmin.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import './LocalStoreAdmin.scss';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { http, useNavigationService, AlertService, FxCurrency } from '../fx-core';
import {
  AppBar, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Paper, Button, TextField, Typography,
  Chip, Box, Badge, CircularProgress, Tooltip, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton
} from '@material-ui/core';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import ContentCopyIcon from '@material-ui/icons/FileCopy';
import PaymentOutlinedIcon from '@material-ui/icons/PaymentOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import CropFreeIcon from '@material-ui/icons/CropFree';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import BarChartIcon from '@material-ui/icons/BarChart';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';
import { NoRecords } from '../NoRecords/NoRecords';

 interface PaymentRow {
  id: number;
  merchantName: string;
  bookingType: string;
  slots: { slotDate: string; slotLabel: string }[];
  slotCount: number;
  amountPaid: number;
  couponCode: string | null;
  discountAmount: number;
  referenceNumber: string;
  paymentScreenshotUrl: string | null;
  status: 'Pending' | 'Paid' | 'Rejected';
  submittedDate: string;
}

interface SelectedCommunity {
  id: number;
  name: string;
}

interface RackOption {
  id: number;
  label: string;
  locationNote?: string;
}

interface CouponOption {
  id: number;
  code: string;
  discountAmount: number;
}

type QrSize = 'small' | 'medium' | 'large';
type StatusFilter = 'All' | 'Pending' | 'Paid' | 'Rejected';

const QR_PIXEL_SIZES: Record<QrSize, number> = {
  small: 160,
  medium: 320,
  large: 480
};

const STATUS_FILTERS: StatusFilter[] = ['All', 'Pending', 'Paid', 'Rejected'];
const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  All: 'All',
  Pending: 'Pending',
  Paid: 'Approved',
  Rejected: 'Rejected'
};

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  requireReason?: boolean;
  onConfirm: (reason?: string) => void;
}

const EMPTY_CONFIRM: ConfirmState = {
  open: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  onConfirm: () => {}
};

function a11yProps(index: number) {
  return { id: `localstore-tab-${index}`, 'aria-controls': `localstore-tabpanel-${index}` };
}

const LocalStoreAdmin = (props: any) => {
  const { pageState } = useNavigationService();
  const { communityId: communityIdParam } = useParams<{ communityId: string }>();

  const [selCommunity, setSelCommunity] = useState<SelectedCommunity>(
    pageState?.selCommunity || { id: parseInt(communityIdParam) || 0, name: '' }
  );

  const [tabValue, setTabValue] = useState(0);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // ── Payments — search, filter & pagination ────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [hourlyRate, setHourlyRate] = useState<number>(0);
  const [rateInput, setRateInput] = useState<string>('');
  const [savingRate, setSavingRate] = useState(false);
  const [loadingRate, setLoadingRate] = useState(false);

  // ── QR Code tab state ─────────────────────────────────────────────
  const [qrToken, setQrToken] = useState<string>('');
  const [qrUrl, setQrUrl] = useState<string>('');
  const [loadingQr, setLoadingQr] = useState(false);
  const [qrSize, setQrSize] = useState<QrSize>('medium');
  const [showRawToken, setShowRawToken] = useState(false);
  const qrWrapRef = useRef<HTMLDivElement>(null);

  // ── Racks (community-scoped, managed in Settings) ────────────────
  const [racks, setRacks] = useState<RackOption[]>([]);
  const [loadingRacks, setLoadingRacks] = useState(false);
  const [newRackLabel, setNewRackLabel] = useState('');
  const [newRackLocationNote, setNewRackLocationNote] = useState('');
  const [savingRack, setSavingRack] = useState(false);

  // Coupons are fixed-amount discounts scoped to this community.
  const [coupons, setCoupons] = useState<CouponOption[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponAmount, setCouponAmount] = useState('');
  const [savingCoupon, setSavingCoupon] = useState(false);

  // ── Shared confirmation dialog (used for destructive actions) ─────
  const [confirm, setConfirm] = useState<ConfirmState>(EMPTY_CONFIRM);
  const [confirmReason, setConfirmReason] = useState('');
  const closeConfirm = () => {
    setConfirm(EMPTY_CONFIRM);
    setConfirmReason('');
  };

  // ── Fallback: resolve community details when landing via direct URL/refresh
  // (no pageState available, e.g. someone pasted the link or hit refresh) ──
  useEffect(() => {
    if (!pageState?.selCommunity && communityIdParam) {
      http.get({ url: `community/${communityIdParam}` })
        .then((res: any) => {
          setSelCommunity({ id: res.data.id, name: res.data.name });
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityIdParam]);

  // ── Payments ──────────────────────────────────────────────────────
  const getPayments = () => {
    if (!selCommunity.id) return;
    setLoadingPayments(true);
    http.get({ url: `localstore/community/${selCommunity.id}/payments` })
      .then((res: any) => setPayments(res.data.payments || []))
      .catch(() => setPayments([]))
      .finally(() => setLoadingPayments(false));
  };

  const approvePayment = (id: number) => {
    http.post({ url: `localstore/payment/${id}/approve` }).then(() => {
      AlertService.showSuccessMsg('Payment approved');
      getPayments();
    }).catch(() => AlertService.showMsg({ message: 'Could not approve payment' }));
  };

  const rejectPayment = (id: number, reason: string) => {
    http.post({
      url: `localstore/payment/${id}/reject`,
      data: { reason }
    }).then(() => {
      AlertService.showSuccessMsg('Payment rejected');
      getPayments();
    }).catch(() => AlertService.showMsg({ message: 'Could not reject payment' }));
  };

  const requestRejectPayment = (row: PaymentRow) => {
    setConfirmReason('');
    setConfirm({
      open: true,
      title: 'Reject this payment?',
      message: `${row.merchantName}'s ${row.bookingType.toLowerCase()} booking (${row.slots.length} slot${row.slots.length !== 1 ? 's' : ''}) will be marked as rejected. Let the merchant know before doing this if they've already set up their stall.`,
      confirmLabel: 'Reject Payment',
      destructive: true,
      requireReason: true,
      onConfirm: (reason?: string) => {
        rejectPayment(row.id, reason!.trim());
        closeConfirm();
      }
    });
  };

  // TODO: wire this up to the actual analytics view/route once it exists.
  const openAnalytics = (row: PaymentRow) => {
    AlertService.showMsg({ message: `Analytics for ${row.merchantName} — coming soon` });
  };

  // ── Hourly rate ───────────────────────────────────────────────────
  const getHourlyRate = () => {
    if (!selCommunity.id) return;
    setLoadingRate(true);
    http.get({ url: `localstore/community/${selCommunity.id}/hourlyrate` })
      .then((res: any) => {
        const rate = res.data?.rate ?? 0;
        setHourlyRate(rate);
        setRateInput(String(rate));
      })
      .catch(() => { setHourlyRate(0); setRateInput(''); })
      .finally(() => setLoadingRate(false));
  };

  const parsedRate = parseFloat(rateInput);
  const rateIsInvalid = rateInput !== '' && (isNaN(parsedRate) || parsedRate <= 0);

  const saveHourlyRate = () => {
    if (isNaN(parsedRate) || parsedRate <= 0) {
      AlertService.showMsg({ message: 'Please enter a valid rate' });
      return;
    }
    setSavingRate(true);
    http.post({ url: `localstore/community/${selCommunity.id}/hourlyrate`, data: { rate: parsedRate } })
      .then(() => {
        AlertService.showSuccessMsg('Hourly rate updated');
        setHourlyRate(parsedRate);
      })
      .catch(() => AlertService.showMsg({ message: 'Could not save rate' }))
      .finally(() => setSavingRate(false));
  };

  // ── QR Code ───────────────────────────────────────────────────────
  const getQrCode = () => {
    if (!selCommunity.id) return;
    setLoadingQr(true);
    http.get({ url: `localstore/community/${selCommunity.id}/qrcode` })
      .then((res: any) => {
        setQrToken(res.data?.token || '');
        setQrUrl(res.data?.qrUrl || res.data?.token || '');
      })
      .catch(() => { setQrToken(''); setQrUrl(''); })
      .finally(() => setLoadingQr(false));
  };

  const copyToken = () => {
    if (!qrToken) return;
    try {
      navigator.clipboard.writeText(qrToken);
      AlertService.showSuccessMsg('Token copied to clipboard');
    } catch {
      AlertService.showMsg({ message: 'Could not copy — select and copy manually' });
    }
  };

  const printQrCode = () => {
    window.print();
  };

  // Renders the on-screen QR (SVG) to a PNG and triggers a download, so
  // admins can share/print the code without relying on the browser's print dialog.
  const downloadQrCode = () => {
    const svgEl = qrWrapRef.current?.querySelector('svg');
    if (!svgEl) return;

    const svgString = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const padding = 32;
      const canvas = document.createElement('canvas');
      canvas.width = img.width + padding * 2;
      canvas.height = img.height + padding * 2 + 40; // extra room for the label
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, padding, padding);
        ctx.fillStyle = '#333333';
        ctx.font = '600 16px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(selCommunity.name || '', canvas.width / 2, canvas.height - 16);
      }

      URL.revokeObjectURL(svgUrl);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `${(selCommunity.name || 'localstore').replace(/\s+/g, '-').toLowerCase()}-qrcode.png`;
        link.click();
        URL.revokeObjectURL(pngUrl);
      });
    };
    img.src = svgUrl;
  };

  // ── Racks ─────────────────────────────────────────────────────────
  const getRacks = () => {
    if (!selCommunity.id) return;
    setLoadingRacks(true);
    http.get({ url: `localstore/community/${selCommunity.id}/racks` })
      .then((res: any) => setRacks(res.data?.racks || []))
      .catch(() => setRacks([]))
      .finally(() => setLoadingRacks(false));
  };

  const handleAddRack = () => {
    if (!newRackLabel.trim()) {
      AlertService.showMsg({ message: 'Please enter a rack label' });
      return;
    }
    setSavingRack(true);
    http.post({
      url: `localstore/community/${selCommunity.id}/racks`,
      data: { label: newRackLabel.trim(), locationNote: newRackLocationNote.trim() }
    })
      .then(() => {
        AlertService.showSuccessMsg('Rack added');
        setNewRackLabel('');
        setNewRackLocationNote('');
        getRacks();
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.error || 'Could not add rack';
        AlertService.showMsg({ message: msg });
      })
      .finally(() => setSavingRack(false));
  };

  const removeRack = (rackId: number) => {
    http.post({ url: `localstore/rack/${rackId}/remove`, data: null })
      .then(() => {
        AlertService.showSuccessMsg('Rack removed');
        getRacks();
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.error || "Move or remove this rack's products first";
        AlertService.showMsg({ message: msg });
      });
  };

  const requestRemoveRack = (rack: RackOption) => {
    setConfirm({
      open: true,
      title: 'Remove this rack?',
      message: `"${rack.label}" will no longer be available for merchants to assign products to. Move any products on it first, or the removal may fail.`,
      confirmLabel: 'Remove Rack',
      destructive: true,
      onConfirm: () => {
        removeRack(rack.id);
        closeConfirm();
      }
    });
  };

  const getCoupons = () => {
    if (!selCommunity.id) return;
    setLoadingCoupons(true);
    http.get({ url: `localstore/community/${selCommunity.id}/coupons` })
      .then((res: any) => setCoupons(res.data?.coupons || []))
      .catch(() => setCoupons([]))
      .finally(() => setLoadingCoupons(false));
  };

  const handleAddCoupon = () => {
    const discountAmount = parseFloat(couponAmount);
    if (!couponCode.trim() || isNaN(discountAmount) || discountAmount <= 0) {
      AlertService.showMsg({ message: 'Enter a coupon code and a discount greater than 0' });
      return;
    }
    setSavingCoupon(true);
    http.post({
      url: `localstore/community/${selCommunity.id}/coupons`,
      data: { code: couponCode.trim().toUpperCase(), discountAmount }
    })
      .then((res: any) => {
        if (!res.data?.success) throw new Error(res.data?.error || 'Could not create coupon');
        AlertService.showSuccessMsg('Coupon created');
        setCouponCode('');
        setCouponAmount('');
        getCoupons();
      })
      .catch((err: any) => AlertService.showMsg({ message: err?.message || 'Could not create coupon' }))
      .finally(() => setSavingCoupon(false));
  };

  const removeCoupon = (coupon: CouponOption) => {
    setConfirm({
      open: true,
      title: 'Remove this coupon?',
      message: `Coupon ${coupon.code} will no longer be accepted for new Local Store bookings.`,
      confirmLabel: 'Remove Coupon',
      destructive: true,
      onConfirm: () => {
        http.post({ url: `localstore/coupon/${coupon.id}/remove`, data: null })
          .then(() => { AlertService.showSuccessMsg('Coupon removed'); getCoupons(); })
          .catch(() => AlertService.showMsg({ message: 'Could not remove coupon' }));
        closeConfirm();
      }
    });
  };

  useEffect(() => {
    getPayments();
    getHourlyRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selCommunity.id]);

  useEffect(() => {
    if (tabValue === 1) getRacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, selCommunity.id]);

  useEffect(() => {
    if (tabValue === 2) getQrCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, selCommunity.id]);

  useEffect(() => {
    if (tabValue === 3) getCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, selCommunity.id]);

  // Reset to page 1 whenever the visible result set changes shape.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter]);

  // ── Derived data for the Payments tab ──────────────────────────────
  const paymentStats = useMemo(() => {
    const pending = payments.filter((p) => p.status === 'Pending').length;
    const approved = payments.filter((p) => p.status === 'Paid');
    const rejected = payments.filter((p) => p.status === 'Rejected').length;
    const totalCollected = approved.reduce((sum, p) => sum + (p.amountPaid || 0), 0);
    return { pending, approvedCount: approved.length, rejected, totalCollected };
  }, [payments]);

  const filteredPayments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return payments.filter((p) => {
      const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
      const matchesSearch = !term
        || p.merchantName.toLowerCase().includes(term)
        || (p.couponCode || '').toLowerCase().includes(term)
        || p.referenceNumber.toLowerCase().includes(term);
      return matchesStatus && matchesSearch;
    });
  }, [payments, statusFilter, searchTerm]);

  const pagedPayments = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPayments.slice(start, start + rowsPerPage);
  }, [filteredPayments, page, rowsPerPage]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
  };

  return (
    <div className="localstoreadmin-ui pt-5">
      <div className="pl-4 pt-4 no-print">
        <Typography variant="h5"><b>Local Store — {selCommunity.name || 'Select a community'}</b></Typography>
        <Typography className="addresscolor font-14 pt-1">
          Manage stall booking payments, racks, and the space-rental QR code for this community.
        </Typography>
      </div>

      <div className="px-4 pt-3">
        <div className="no-print">
          <AppBar position="static" color="default">
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                icon={
                  <Badge badgeContent={paymentStats.pending} color="error">
                    <PaymentOutlinedIcon fontSize="small" />
                  </Badge>
                }
                label="Payments"
                {...a11yProps(0)}
              />
              <Tab icon={<SettingsOutlinedIcon fontSize="small" />} label="Settings" {...a11yProps(1)} />
              <Tab icon={<CropFreeIcon fontSize="small" />} label="QR Code" {...a11yProps(2)} />
              <Tab icon={<LocalOfferIcon fontSize="small" />} label="Coupons" {...a11yProps(3)} />
            </Tabs>
          </AppBar>
        </div>

        {tabValue === 0 && (
          <div className="pt-4 no-print">
            <Paper className="payments-panel" elevation={0}>
              <Box className="stats-row">
                <Box className="stat-card stat-pending">
                  <Typography className="stat-value">{paymentStats.pending}</Typography>
                  <Typography className="stat-label">Pending review</Typography>
                </Box>
                <Box className="stat-card stat-approved">
                  <Typography className="stat-value">{paymentStats.approvedCount}</Typography>
                  <Typography className="stat-label">Approved</Typography>
                </Box>
                <Box className="stat-card stat-rejected">
                  <Typography className="stat-value">{paymentStats.rejected}</Typography>
                  <Typography className="stat-label">Rejected</Typography>
                </Box>
                <Box className="stat-card stat-revenue">
                  <Typography className="stat-value"><FxCurrency value={paymentStats.totalCollected} /></Typography>
                  <Typography className="stat-label">Total collected</Typography>
                </Box>
              </Box>

              <Box className="payments-toolbar">
                <TextField
                  placeholder="Search by merchant or reference #"
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="payments-search"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />

                <Box className="status-filter-group" role="group" aria-label="Filter by status">
                  {STATUS_FILTERS.map((s) => {
                    const count = s === 'All' ? payments.length : payments.filter((p) => p.status === s).length;
                    return (
                      <Chip
                        key={s}
                        label={`${STATUS_FILTER_LABELS[s]} (${count})`}
                        size="small"
                        onClick={() => setStatusFilter(s)}
                        className={`status-filter-chip${statusFilter === s ? ' active' : ''}`}
                      />
                    );
                  })}
                </Box>

                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={getPayments} aria-label="Refresh payments">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {loadingPayments && (
                <Box className="loading-block">
                  <CircularProgress size={26} />
                  <Typography className="addresscolor font-13 pt-2">Loading payments…</Typography>
                </Box>
              )}

              {!loadingPayments && payments.length === 0 && <NoRecords />}

              {!loadingPayments && payments.length > 0 && filteredPayments.length === 0 && (
                <Box className="empty-state">
                  <InboxOutlinedIcon className="empty-state-icon" />
                  <Typography className="font-14"><b>No payments match your filters</b></Typography>
                  <Typography className="addresscolor font-13 pb-2">
                    Try a different search term or status.
                  </Typography>
                  <Button size="small" onClick={clearFilters}>Clear filters</Button>
                </Box>
              )}

              {!loadingPayments && filteredPayments.length > 0 && (
                <>
                  <TableContainer>
                    <Table size="small" className="table-striped">
                      <TableHead>
                        <TableRow>
                          <TableCell>Merchant</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Slots</TableCell>
                          <TableCell>Amount Paid</TableCell>
                          <TableCell>Coupon</TableCell>
                          <TableCell>Reference #</TableCell>
                          <TableCell>Payment Proof</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell className="action-col">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedPayments.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.merchantName}</TableCell>
                            <TableCell>
                              <Chip label={row.bookingType} size="small" className="booking-type-chip" />
                            </TableCell>
                            <TableCell>
                              <div className="slot-breakdown-cell">
                                {row.slots.slice(0, 3).map((s, i) => (
                                  <div key={i} className="slot-breakdown-line">{s.slotDate} · {s.slotLabel}</div>
                                ))}
                                {row.slots.length > 3 && (
                                  <div className="slot-breakdown-more">+{row.slots.length - 3} more</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell><FxCurrency value={row.amountPaid} /></TableCell>
                            <TableCell>
                              {row.couponCode ? (
                                <div className="coupon-payment-cell">
                                  <Chip label={row.couponCode} size="small" className="coupon-code-chip" />
                                  <span className="coupon-discount-value">−<FxCurrency value={row.discountAmount || 0} /></span>
                                </div>
                              ) : (
                                <span className="no-screenshot-text">—</span>
                              )}
                            </TableCell>
                            <TableCell>{row.referenceNumber}</TableCell>
                            <TableCell>
                              {row.paymentScreenshotUrl ? (
                                <a href={row.paymentScreenshotUrl} target="_blank" rel="noreferrer" className="screenshot-thumb-link">
                                  <img src={row.paymentScreenshotUrl} alt="Payment proof" className="screenshot-thumb" />
                                </a>
                              ) : (
                                <span className="no-screenshot-text">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.status === 'Paid' ? 'Approved' : row.status}
                                size="small"
                                icon={
                                  row.status === 'Paid' ? <CheckCircleOutlineIcon fontSize="small" /> :
                                  row.status === 'Rejected' ? <HighlightOffIcon fontSize="small" /> : undefined
                                }
                                className={
                                  row.status === 'Paid' ? 'status-chip-approved' :
                                  row.status === 'Rejected' ? 'status-chip-rejected' :
                                  'status-chip-pending'
                                }
                              />
                            </TableCell>
                            <TableCell className="action-cell">
                              {row.status === 'Pending' && (
                                <Box display="flex" alignItems="center">
                                  <Button
                                    size="small" variant="outlined" className="approve-btn"
                                    startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                                    onClick={() => approvePayment(row.id)}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="small" variant="outlined" className="reject-btn"
                                    startIcon={<HighlightOffIcon fontSize="small" />}
                                    onClick={() => requestRejectPayment(row)}
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              )}
                              {row.status === 'Paid' && (
                                <Button size="small" variant="outlined" startIcon={<BarChartIcon fontSize="small" />} onClick={() => openAnalytics(row)}>
                                  Analytics
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={filteredPayments.length}
                    page={page}
                    onChangePage={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </>
              )}
            </Paper>
          </div>
        )}

        {tabValue === 1 && (
          <div className="pt-4 no-print settings-tab-grid">
            <Box className="settings-card">
              <Typography className="settings-card-title">Hourly Rate Configuration</Typography>
              <Typography className="settings-card-desc">
                This rate applies only to <b>{selCommunity.name}</b> and determines the space-rental
                cost merchants pay when booking a Local Store slot here.
              </Typography>
              <TextField
                label="Hourly Rate"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                disabled={loadingRate}
                error={rateIsInvalid}
                helperText={rateIsInvalid ? 'Enter an amount greater than 0' : ' '}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
              <Button
                variant="contained"
                className="save-rate-btn"
                onClick={saveHourlyRate}
                disabled={savingRate || loadingRate || !rateInput || rateIsInvalid || parsedRate === hourlyRate}
              >
                {savingRate ? 'Saving…' : 'Save Rate'}
              </Button>
              {hourlyRate > 0 && (
                <Typography className="current-rate-note" component="div">
                  Current rate: <FxCurrency value={hourlyRate} />/hr
                </Typography>
              )}
            </Box>

            <Box className="settings-card">
              <Typography className="settings-card-title">Racks</Typography>
              <Typography className="settings-card-desc">
                These are the physical racks available at <b>{selCommunity.name}</b>. Every
                merchant with an approved stall here picks from this same shared set when
                adding products — they can't create their own.
              </Typography>

              {loadingRacks && (
                <Box className="loading-block loading-block-compact">
                  <CircularProgress size={22} />
                </Box>
              )}

              {!loadingRacks && racks.length === 0 && (
                <Typography className="no-racks-text">No racks set up yet — add the first one below.</Typography>
              )}

              {!loadingRacks && racks.map((rack) => (
                <Box key={rack.id} className="rack-row">
                  <div>
                    <Typography className="rack-name">{rack.label}</Typography>
                    {rack.locationNote && (
                      <Typography className="rack-note">{rack.locationNote}</Typography>
                    )}
                  </div>
                  <Tooltip title="Remove rack">
                    <IconButton size="small" onClick={() => requestRemoveRack(rack)} aria-label={`Remove ${rack.label}`}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}

              <Box className="add-rack-section">
                <Typography className="add-rack-title">Add a rack</Typography>
                <TextField
                  label="Rack Label"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  value={newRackLabel}
                  onChange={(e) => setNewRackLabel(e.target.value)}
                />
                <TextField
                  label="Location Note (optional)"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  value={newRackLocationNote}
                  onChange={(e) => setNewRackLocationNote(e.target.value)}
                />
                <Button
                  variant="outlined"
                  size="small"
                  className="add-rack-btn"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={handleAddRack}
                  disabled={savingRack}
                >
                  {savingRack ? 'Adding…' : 'Add Rack'}
                </Button>
              </Box>
            </Box>
          </div>
        )}

        {tabValue === 2 && (
          <div className="pt-4">
            {loadingQr && (
              <Box className="loading-block no-print">
                <CircularProgress size={26} />
                <Typography className="addresscolor font-13 pt-2">Loading QR code…</Typography>
              </Box>
            )}

            {!loadingQr && !qrToken && (
              <Box className="empty-state no-print">
                <ErrorOutlineIcon className="empty-state-icon" />
                <Typography className="font-14"><b>No QR code yet</b></Typography>
                <Typography className="addresscolor font-13">
                  Check back once this community's space-rental listing is set up.
                </Typography>
              </Box>
            )}

            {!loadingQr && qrToken && (
              <div className="qr-panel">
                <div className="no-print">
                  <Typography className="qr-size-label">Print size</Typography>
                  <Box className="qr-size-toggle">
                    {(['small', 'medium', 'large'] as QrSize[]).map((size) => (
                      <Button
                        key={size}
                        size="small"
                        className={`qr-size-btn${qrSize === size ? ' active' : ''}`}
                        onClick={() => setQrSize(size)}
                      >
                        {size.charAt(0).toUpperCase() + size.slice(1)}
                      </Button>
                    ))}
                  </Box>
                </div>

                <div className="qr-print-area text-center" ref={qrWrapRef}>
                  <div className="qr-code-frame">
                    <QRCodeSVG value={qrUrl || qrToken} size={QR_PIXEL_SIZES[qrSize]} />
                  </div>
                  <Typography className="qr-community-label">
                    {selCommunity.name}
                  </Typography>
                </div>

                <div className="qr-actions-row no-print">
                  <Button
                    variant="contained"
                    className="print-qr-btn"
                    startIcon={<PrintIcon fontSize="small" />}
                    onClick={printQrCode}
                  >
                    Print QR Code
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon fontSize="small" />}
                    onClick={downloadQrCode}
                  >
                    Download PNG
                  </Button>
                  <Button
                    variant="outlined"
                    className="show-raw-btn"
                    onClick={() => setShowRawToken(!showRawToken)}
                  >
                    {showRawToken ? 'Hide' : 'Show'} Raw ID
                  </Button>
                </div>

                {showRawToken && (
                  <Box className="raw-token-card no-print">
                    <Typography className="font-13 addresscolor pb-1">
                      Use this ID if you'd rather generate the QR code yourself with another tool.
                      It must be encoded exactly as shown for scans to work.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        value={qrToken}
                        variant="outlined"
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                      <Tooltip title="Copy ID">
                        <IconButton size="small" onClick={copyToken} aria-label="Copy raw ID">
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
              </div>
            )}
          </div>
        )}

        {tabValue === 3 && (
          <div className="pt-4 no-print settings-tab-grid">
            <Box className="settings-card">
              <Typography className="settings-card-title">Create Coupon</Typography>
              <Typography className="settings-card-desc">
                Create fixed-amount discounts for Local Store bookings at <b>{selCommunity.name}</b>.
              </Typography>
              <TextField
                label="Coupon Code"
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                label="Discount Amount"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                margin="dense"
                value={couponAmount}
                onChange={(e) => setCouponAmount(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <Button variant="contained" className="save-rate-btn" startIcon={<AddIcon />} onClick={handleAddCoupon} disabled={savingCoupon}>
                {savingCoupon ? 'Creating…' : 'Create Coupon'}
              </Button>
            </Box>

            <Box className="settings-card">
              <Typography className="settings-card-title">Available Coupons</Typography>
              {loadingCoupons && <Box className="loading-block loading-block-compact"><CircularProgress size={22} /></Box>}
              {!loadingCoupons && coupons.length === 0 && <Typography className="no-racks-text">No coupons created yet.</Typography>}
              {!loadingCoupons && coupons.map(coupon => (
                <Box key={coupon.id} className="rack-row">
                  <div>
                    <Typography className="rack-name">{coupon.code}</Typography>
                    <Typography className="rack-note"><FxCurrency value={coupon.discountAmount} /> discount</Typography>
                  </div>
                  <Tooltip title="Remove coupon">
                    <IconButton size="small" onClick={() => removeCoupon(coupon)} aria-label={`Remove ${coupon.code}`}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </div>
        )}
      </div>

      {/* Shared confirmation dialog for destructive actions (reject payment, remove rack) */}
      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth className="no-print">
        <DialogTitle disableTypography className="lsa-confirm-dialog-title">
          <ErrorOutlineIcon className={confirm.destructive ? 'lsa-confirm-icon-destructive' : 'lsa-confirm-icon'} />
          <Typography variant="h6" component="span">{confirm.title}</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm.message}</DialogContentText>
          {confirm.requireReason && (
            <TextField
              label="Reason for rejection"
              placeholder="Tell the merchant why this payment was rejected"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={confirmReason}
              onChange={(e) => setConfirmReason(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={`${confirmReason.length}/500`}
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button
            variant="contained"
            color={confirm.destructive ? 'secondary' : 'primary'}
            onClick={() => confirm.onConfirm(confirmReason)}
            disabled={!!confirm.requireReason && !confirmReason.trim()}
          >
            {confirm.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LocalStoreAdmin;
